import { useMemo } from "react";
import type { CachedTopic } from "@/types/intent";

/**
 * Generates a display label from resolved topic names.
 * - 0 topics: "All"
 * - 1 topic: topic name
 * - 2+ topics: "X topics"
 *
 * Pure function for use in non-React contexts (e.g., chips.tsx).
 */
export function getTopicLabel(topicNames: string[]): string {
  if (topicNames.length === 0) return "All";
  if (topicNames.length === 1) return topicNames[0];
  return `${topicNames.length} topics`;
}

/**
 * React hook that resolves topic names from cache and generates label.
 * Use in React components for memoized label generation.
 */
export function useTopicLabel(
  topicIds: string[],
  topicsCache: CachedTopic[]
): string {
  return useMemo(() => {
    const names = topicIds.map((id) => {
      const cached = topicsCache.find((t) => t.id === id);
      return cached?.displayName ?? "1 topic";
    });
    return getTopicLabel(names);
  }, [topicIds, topicsCache]);
}
